// src/Publish.js
import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { baseUrl } from '../api';
import {
  Container,
  Paper,
  Typography,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  Button,
  CircularProgress,
  Divider,
  Box,
  Alert,
  Stack,
  FormHelperText
} from '@mui/material';
import SlideshowIcon from '@mui/icons-material/Slideshow';
import { AuthContext } from '../context/AuthContext';
import { useAxiosAuth }  from "../hooks/useAxiosAuth";

export default function Publish() {
  const { user, accessToken, signInWithGoogle } = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState(0);

  // Shared
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [needsReauth, setNeedsReauth] = useState(false);

  // Tab 0: Publish Course
  const [publishings, setPublishings] = useState([]);
  const [useDefaultBucket, setUseDefaultBucket] = useState(true);
  const [customBucket, setCustomBucket] = useState('');
  const [namePrefix, setNamePrefix] = useState('');
  const [sectionPrefix, setSectionPrefix] = useState('');
  const [courseResult, setCourseResult] = useState(null);

  // Tab 1: Quiz → Forms
  const [quizzes, setQuizzes] = useState([]);
  const [selectedQuizId, setSelectedQuizId] = useState('');
  const [publishedQuiz, setPublishedQuiz] = useState(null);

  // Tab 2: Forms → Classroom
  const [quizForms, setQuizForms] = useState([]);
  const [formId, setFormId] = useState('');
  const [announceResult, setAnnounceResult] = useState(null);
  const [classroomId, setClassroomId] = useState('');

 // New: due date defaults to today + 7 days
 const [dueDate, setDueDate] = useState(() => {
   const d = new Date();
   d.setDate(d.getDate() + 7);
   return d.toISOString().split('T')[0];
 });
 // New: total points (leave blank to default)
 const [maxPoints, setMaxPoints] = useState('');
  
  const axiosInstance = useAxiosAuth();

  // load courses once
  useEffect(() => {
    (async () => {
      try {
        const resp = await axiosInstance.get('/courses/');
        setCourses(resp.data);
      } catch {
        setError('Unable to load courses.');
      }
    })();
  }, []);

  // fetch Classroom publishings
  const fetchCoursePublishings = async () => {
    setError('');
    try {
      const resp = await axiosInstance.get('/classroom/get_publishings/', {
        params: { course_id: selectedCourse },
      });
      setPublishings(resp.data);
    } catch {
      setError('Unable to load published courses.');
    }
  };

  const fetchQuizForms = async () => {
    try {
      const resp = await axiosInstance.get(`classroom/quiz_assessments/${selectedCourse}`);
      setQuizForms(resp.data);
    } catch {
      setError('Unable to load available Forms.');
    }
  };

  // ── TAB 0 HANDLERS ──────────────────────────────────────────────────────────────

  const sendCoursePublish = token =>
    axiosInstance.post(
      '/classroom/publish_course/',
      null,
      {
        params: {
          course_id: selectedCourse,
          ...(useDefaultBucket ? {} : { classroom_bucket: customBucket.trim() }),
          ...(namePrefix.trim()    ? { name_prefix: namePrefix.trim() }         : {}),
          ...(sectionPrefix.trim() ? { section_prefix: sectionPrefix.trim() }   : {}),
        },
        headers: { 'X-GOOGLE-OAUTH-TOKEN': token },
      }
    );

  const sendQuizToForms = token =>
    axiosInstance.post(
      `/classroom/quiz_assessments/create_form/${selectedQuizId}`,
      null,
      { headers: { 'X-GOOGLE-OAUTH-TOKEN': token } }
    );

  const sendAnnounce = token =>
    axiosInstance.post(
      `/classroom/quiz_assessments/announce/`,
      null,
      {
        params: {
          course_id:          selectedCourse,
          platform_course_id: classroomId.trim(),
          form_id:            formId.trim(),
          ...(dueDate       ? { due_date: dueDate }       : {}),
          ...(maxPoints     ? { max_points: maxPoints }   : {}),
        },
        headers: { 'X-GOOGLE-OAUTH-TOKEN': token }
      }
    );
  

  const handleCoursePublish = async () => {
    setError(''); setCourseResult(null); setNeedsReauth(false);
    if (!selectedCourse) {
      setError('Please select a course.');
      return;
    }
    if (!useDefaultBucket && !customBucket.trim()) {
      setError('Please enter your bucket name.');
      return;
    }

    setLoading(true);
    try {
      const token = accessToken || await signInWithGoogle();
      const resp  = await sendCoursePublish(token);
      setCourseResult(resp.data);
      await fetchCoursePublishings();
    } catch (err) {
      if (err.response?.status === 401) {
        setError('Your Google Classroom session has expired.');
        setNeedsReauth(true);
      } else {
        setError(err.response?.data?.detail || 'Publish failed.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCourseReauth = async () => {
    setError(''); setNeedsReauth(false); setLoading(true);
    try {
      const newToken = await signInWithGoogle();
      if (!newToken) throw new Error();
      const retry = await sendCoursePublish(newToken);
      setCourseResult(retry.data);
      await fetchCoursePublishings();
    } catch {
      setError('Re-authentication failed.');
      setNeedsReauth(true);
    } finally {
      setLoading(false);
    }
  };

  const handleCourseUpdate = async platformCourseId => {
    setError(''); setCourseResult(null); setNeedsReauth(false); setLoading(true);
    try {
      const token = accessToken || await signInWithGoogle();
      await axiosInstance.post(
        '/classroom/update_publishing/',
        null,
        {
          params: {
            course_id: selectedCourse,
            platform_course_id: platformCourseId,
            ...(useDefaultBucket ? {} : { classroom_bucket: customBucket.trim() })
          },
          headers: { 'X-GOOGLE-OAUTH-TOKEN': token }
        }
      );
      await fetchCoursePublishings();
    } catch (err) {
      if (err.response?.status === 401) {
        setError('Session expired.');
        setNeedsReauth(true);
      } else {
        setError(err.response?.data?.detail || 'Update failed.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCourseDelete = async platformCourseId => {
    setError(''); setLoading(true);
    try {
      await axiosInstance.delete('/classroom/delete_publishing/', {
        params: {
          course_id: selectedCourse,
          platform_course_id: platformCourseId
        }
      });
      await fetchCoursePublishings();
    } catch {
      setError('Delete failed.');
    } finally {
      setLoading(false);
    }
  };

  // reload publishings on tab 0 or course change
  useEffect(() => {
    if (activeTab === 0) {
      if (selectedCourse) fetchCoursePublishings();
      else setPublishings([]);
    }
  }, [activeTab, selectedCourse]);

  // ── TAB 1 HANDLERS ──────────────────────────────────────────────────────────────

  const fetchQuizzes = async () => {
    setError('');
    try {
      const resp = await axiosInstance.get(`/exams/quizzes/${selectedCourse}`);
      setQuizzes(resp.data);
    } catch {
      setError('Unable to load quizzes.');
    }
  };

  const handlePublishQuizToForms = async () => {
    setError(''); setPublishedQuiz(null); setNeedsReauth(false);
    if (!selectedCourse) {
      setError('Please select a course.');
      return;
    }
    if (!selectedQuizId) {
      setError('Please select a quiz.');
      return;
    }

    setLoading(true);
    try {
      const token = accessToken || await signInWithGoogle();
      const resp  = await sendQuizToForms(token);
      setPublishedQuiz(resp.data);
    } catch (err) {
      if (err.response?.status === 401) {
        setError('Session expired.');
        setNeedsReauth(true);
      } else {
        setError(err.response?.data?.detail || 'Publish failed.');
      }
    } finally {
      setLoading(false);
    }
  };

  // 3. New re-auth handler for Tab 1
  const handleQuizReauth = async () => {
    setError(''); setNeedsReauth(false); setLoading(true);
    try {
      const newToken = await signInWithGoogle();
      const resp     = await sendQuizToForms(newToken);
      setPublishedQuiz(resp.data);
    } catch {
      setError('Re-authentication failed.');
      setNeedsReauth(true);
    } finally {
      setLoading(false);
    }
  };
  
  // reload quizzes on tab 1 or course change
  useEffect(() => {
    if (activeTab === 1) {
      setPublishedQuiz(null);
      setError('');
      if (selectedCourse) fetchQuizzes();
      else {
        setQuizzes([]);
        setSelectedQuizId('');
      }
    }
  }, [activeTab, selectedCourse]);

  // keep formId in sync when you just published a quiz
  useEffect(() => {
    if (publishedQuiz?.form_id) {
      setFormId(publishedQuiz.form_id);
    }
  }, [publishedQuiz]);

  // ── TAB 2 HANDLERS ──────────────────────────────────────────────────────────────

  const handleAnnounceQuizToClassroom = async () => {
    setError(''); setAnnounceResult(null); setNeedsReauth(false);

    if (!selectedCourse) {
      setError('Please select a course.');
      return;
    }
    if (!formId.trim()) {
      setError('Please enter the Form ID.');
      return;
    }
    if (!classroomId.trim()) {
      setError('Please select a Classroom.');
      return;
    }

    setLoading(true);
    try {
      const token = accessToken || await signInWithGoogle();
      const resp  = await sendAnnounce(token);
      setAnnounceResult(resp.data);
    } catch (err) {
      if (err.response?.status === 401) {
        setError('Session expired.');
        setNeedsReauth(true);
      } else {
        setError(err.response?.data?.detail || 'Announce failed.');
      }
    } finally {
      setLoading(false);
    }
  };


  // 3. New re-auth handler for Tab 2
  const handleAnnounceReauth = async () => {
    setError(''); setNeedsReauth(false); setLoading(true);
    try {
      const newToken = await signInWithGoogle();
      const resp     = await sendAnnounce(newToken);
      setAnnounceResult(resp.data);
    } catch {
      setError('Re-authentication failed.');
      setNeedsReauth(true);
    } finally {
      setLoading(false);
    }
  };
  

  // reload publishings & reset errors when tab 2 or course changes
  useEffect(() => {
    if (activeTab === 2) {
      setError('');
      setAnnounceResult(null);
      if (selectedCourse) {
        fetchCoursePublishings();
        fetchQuizForms();
      }
    }
  }, [activeTab, selectedCourse]);

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          sx={{ mb: 3 }}
        >
          <Tab label="Publish Course" />
          <Tab label="Quiz → Forms" />
          <Tab label="Forms → Classroom" />
        </Tabs>

        {/* ── TAB 0: Publish Course ─────────────────────────────────────────────── */}
        {activeTab === 0 && (
          <>
            <Typography variant="h5" gutterBottom>
              Publish Course to Google Classroom
            </Typography>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {needsReauth && (
              <Box sx={{ mb: 2, textAlign: 'center' }}>
                <Button
                  variant="outlined"
                  onClick={handleCourseReauth}
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={20} /> : 'Re-authenticate'}
                </Button>
              </Box>
            )}
            {courseResult && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {courseResult.message} (Classroom ID: {courseResult.google_classroom_course_id})
              </Alert>
            )}

            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel id="course-select-label">Select Course</InputLabel>
              <Select
                labelId="course-select-label"
                value={selectedCourse}
                label="Select Course"
                onChange={e => setSelectedCourse(e.target.value)}
              >
                <MenuItem value="">-- Select a Course --</MenuItem>
                {courses.map(c => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.subject}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl component="fieldset" sx={{ mb: 3 }}>
              <RadioGroup
                row
                value={useDefaultBucket ? 'default' : 'custom'}
                onChange={e => setUseDefaultBucket(e.target.value === 'default')}
              >
                <FormControlLabel
                  value="default"
                  control={<Radio />}
                  label="Use default bucket"
                />
                <FormControlLabel
                  value="custom"
                  control={<Radio />}
                  label="Use my bucket"
                />
              </RadioGroup>
            </FormControl>

            {!useDefaultBucket && (
              <TextField
                fullWidth
                sx={{ mb: 3 }}
                label="My Bucket Name"
                value={customBucket}
                onChange={e => setCustomBucket(e.target.value)}
                helperText="Enter your GCS bucket name"
              />
            )}

            <TextField
              fullWidth
              sx={{ mb: 3 }}
              label="Name Prefix"
              value={namePrefix}
              onChange={e => setNamePrefix(e.target.value)}
              helperText="Optional prefix for course name"
            />
            <TextField
              fullWidth
              sx={{ mb: 3 }}
              label="Section Prefix"
              value={sectionPrefix}
              onChange={e => setSectionPrefix(e.target.value)}
              helperText="Optional prefix for course section"
            />

            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Button
                variant="contained"
                size="large"
                onClick={handleCoursePublish}
                disabled={loading || !selectedCourse}
                startIcon={loading ? <CircularProgress size={20} /> : <SlideshowIcon />}
              >
                {loading ? 'Publishing...' : 'Publish'}
              </Button>
            </Box>

            <Divider />

            <Box sx={{ mt: 4 }}>
              <Typography variant="h6" gutterBottom>
                Published Courses
              </Typography>
              {publishings.length === 0 ? (
                <Typography color="text.secondary">
                  {selectedCourse
                    ? 'No published courses yet.'
                    : 'Select a course above to see its published versions.'}
                </Typography>
              ) : (
                <Stack spacing={2}>
                  {publishings.map(pub => (
                    <Paper
                      key={pub.platform_course_id}
                      variant="outlined"
                      sx={{
                        p: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                      }}
                    >
                      <Box>
                        <Typography>
                          <strong>ID:</strong> {pub.platform_course_id}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {pub.platform} • {new Date(pub.published_at).toLocaleString()}
                        </Typography>
                      </Box>
                      <Box>
                        <Button
                          size="small"
                          onClick={() => handleCourseUpdate(pub.platform_course_id)}
                          disabled={loading}
                          sx={{ mr: 1 }}
                        >
                          Update
                        </Button>
                        <Button
                          size="small"
                          color="error"
                          onClick={() => handleCourseDelete(pub.platform_course_id)}
                          disabled={loading}
                        >
                          Delete
                        </Button>
                      </Box>
                    </Paper>
                  ))}
                </Stack>
              )}
            </Box>
          </>
        )}

        {/* ── TAB 1: Quiz → Forms ───────────────────────────────────────────────────────── */}
        {activeTab === 1 && (
          <>
            <Typography variant="h5" gutterBottom>
              Quiz → Google Forms
            </Typography>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {needsReauth && (
              <Box textAlign="center" mb={2}>
                <Button onClick={handleQuizReauth} disabled={loading}>
                  {loading ? <CircularProgress size={20}/> : 'Re-authenticate'}
                </Button>
              </Box>
            )}
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel id="quiz-course-label">Select Course</InputLabel>
              <Select
                labelId="quiz-course-label"
                value={selectedCourse}
                label="Select Course"
                onChange={e => setSelectedCourse(e.target.value)}
              >
                <MenuItem value="">-- Select a Course --</MenuItem>
                {courses.map(c => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.subject}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel id="quiz-select-label">Select Quiz</InputLabel>
              <Select
                labelId="quiz-select-label"
                value={selectedQuizId}
                label="Select Quiz"
                onChange={e => setSelectedQuizId(e.target.value)}
                disabled={!selectedCourse || quizzes.length === 0}
              >
                <MenuItem value="">-- Select a Quiz --</MenuItem>
                {quizzes.map(q => (
                  <MenuItem key={q.id} value={q.id}>
                    {q.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              variant="contained"
              fullWidth
              onClick={handlePublishQuizToForms}
              disabled={!selectedQuizId || loading}
              startIcon={loading && <CircularProgress size={20} />}
            >
              {loading ? 'Publishing...' : 'Publish to Forms'}
            </Button>

            {publishedQuiz && (
              <Box sx={{ mt: 3 }}>
                <Alert severity="success">
                  Form published! →{' '}
                  <a
                    href={`https://docs.google.com/forms/d/${publishedQuiz.form_id}/viewform`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Open Form
                  </a>
                </Alert>
              </Box>
            )}
          </>
        )}

        {/* ── TAB 2: Forms → Classroom ─────────────────────────────────────────────────── */}
        {activeTab === 2 && (
          <>
            <Typography variant="h5" gutterBottom>
              Forms → Google Classroom
            </Typography>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {needsReauth && (
              <Box textAlign="center" mb={2}>
                <Button onClick={handleAnnounceReauth} disabled={loading}>
                  {loading ? <CircularProgress size={20}/> : 'Re-authenticate'}
                </Button>
              </Box>
            )}
            {/* course select */}
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel id="form-course-label">Select Course</InputLabel>
              <Select
                labelId="form-course-label"
                value={selectedCourse}
                label="Select Course"
                onChange={e => setSelectedCourse(e.target.value)}
              >
                <MenuItem value="">-- Select a Course --</MenuItem>
                {courses.map(c => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.subject}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* form ID field */}
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel id="form-select-label">
                Select Google Form
              </InputLabel>
              <Select
                labelId="form-select-label"
                value={formId}
                label="Select Google Form"
                onChange={e => setFormId(e.target.value)}
                disabled={!quizForms.length}
              >
                <MenuItem value="">
                  — Pick a Form —  
                </MenuItem>
                {quizForms.map(q => (
                  <MenuItem
                    key={q.visual_id}
                    value={q.form_id}
                  >
                    {q.name} ({q.form_id})
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                Choose a form you’ve already published
              </FormHelperText>
            </FormControl>

            {/* classroom select */}
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel id="classroom-select-label">
                Select Classroom
              </InputLabel>
              <Select
                labelId="classroom-select-label"
                value={classroomId}
                label="Select Classroom"
                onChange={e => setClassroomId(e.target.value)}
                disabled={publishings.length === 0}
              >
                <MenuItem value="">— Select a Classroom —</MenuItem>
                {publishings.map(pub => (
                  <MenuItem
                    key={pub.platform_course_id}
                    value={pub.platform_course_id}
                  >
                    {pub.platform_course_id} ({new Date(pub.published_at).toLocaleString()})
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                Choose one of the published Classroom courses
              </FormHelperText>
            </FormControl>

           {/* New: Due Date picker */}
           <TextField
             fullWidth
             label="Due Date"
             type="date"
             value={dueDate}
             onChange={e => setDueDate(e.target.value)}
             InputLabelProps={{ shrink: true }}
             sx={{ mb: 3 }}
             helperText="Optional – defaults to today + 7 days"
           />
           {/* New: Total Points */}
           <TextField
             fullWidth
             label="Max Points"
             type="number"
             value={maxPoints}
             onChange={e => setMaxPoints(e.target.value)}
             sx={{ mb: 3 }}
             helperText="Optional – defaults to number of questions"
           />
           <Button
              variant="contained"
              fullWidth
              onClick={handleAnnounceQuizToClassroom}
              disabled={!selectedCourse || !formId.trim() || !classroomId.trim() || loading}
              startIcon={loading && <CircularProgress size={20} />}
            >
              {loading ? 'Announcing...' : 'Announce to Classroom'}
            </Button>

            {announceResult && (
              <Box sx={{ mt: 3 }}>
                <Alert severity="success">{announceResult.message}</Alert>
              </Box>
            )}
          </>
        )}
      </Paper>
    </Container>
  );
}
